import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Image, Button, Label } from 'semantic-ui-react';

import { useBreakpoints } from '$app/common/utils/mediaQueries';

const EventItem = ({
  name,
  time,
  type,
  duration,
  description,
  tags,
  organizerName,
  organizerLogo,
  registrationLink,
  eventImageLink,
  webinarRecordingLink,
}) => {
  const [showReadMore, setShowReadMore] = useState(true);

  const { isSmDown, isMdDown } = useBreakpoints();

  const truncateString = (text, length = 30) =>
    text.length > length
      ? `${text.slice(0, Math.max(0, length - 3))}...`
      : text;

  return (
    <div
      key={name}
      css={{
        width: '100%',
        display: 'flex',
        flexDirection: isSmDown ? 'column' : 'row',
        marginBottom: '20px',
        border: 'solid 1px #DADDDD',
        borderRadius: '10px',
      }}
    >
      <div
        css={{
          width: '100%',
          display: 'flex',
          flexDirection: isSmDown ? 'column' : 'row',
          padding: '30px',
          flex: 0.75,
        }}
      >
        <Image
          src={eventImageLink}
          css={{
            '&.ui.image': {
              objectFit: 'cover',
              backgroundRepeat: 'no-repeat',
              width: isSmDown ? '100%' : 290,
              height: 290,
            },
          }}
        />
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            marginLeft: isSmDown ? 0 : 30,
            marginTop: isSmDown ? 15 : 0,
          }}
        >
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <p
              css={{
                color: '#6C2D8A',
                fontWeight: 700,
                fontSize: 13,
                margin: 0,
              }}
            >
              {time}
            </p>
            <p
              css={{
                fontSize: '10px',
                fontStyle: 'italic',
                marginLeft: 8,
              }}
            >
              Singapore Time (SGT)
            </p>
          </div>
          <p
            css={{
              fontWeight: 700,
              fontSize: 20,
              marginBottom: 10,
            }}
          >
            {name}
          </p>
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: 10,
            }}
          >
            <Image
              src={
                type === 'Webinar'
                  ? 'https://images.snaphunt.com/assets/images/video.png'
                  : 'https://images.snaphunt.com/assets/images/code.png'
              }
              css={
                type === 'Webinar'
                  ? { width: 12, height: 10 }
                  : { width: 15, height: 12 }
              }
            />
            <p
              css={{
                marginLeft: 5,
                fontSize: 13,
              }}
            >
              Duration {duration} | {type}
            </p>
          </div>
          <p
            css={{
              fontSize: 13,
              margin: 0,
            }}
          >
            {showReadMore
              ? truncateString(description, isSmDown ? 170 : 240)
              : description}
          </p>
          {showReadMore && (
            <div
              tabIndex={-1}
              role="button"
              onClick={() => setShowReadMore(false)}
              onKeyDown={() => setShowReadMore(false)}
              css={{
                color: '#6C2D8A',
                fontSIze: 13,
                marginTop: 5,
                fontStyle: 'italic',
                cursor: 'pointer',
              }}
            >
              read more...
            </div>
          )}
          <div
            css={{
              display: 'flex',
              flexWrap: 'wrap',
              marginTop: 15,
            }}
          >
            {tags.map(tag => (
              <Label
                key={tag}
                basic
                css={{
                  '&.ui.basic.label': {
                    textTransform: 'uppercase',
                    border: '1px solid #000000',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: 10,
                    marginBottom: 5,
                  },
                }}
              >
                {tag}
              </Label>
            ))}
          </div>
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              marginTop: '20px',
            }}
          >
            <Button
              content={
                type !== 'Webinar'
                  ? 'Take the challenge'
                  : webinarRecordingLink
                  ? 'View Recording'
                  : 'Register Now'
              }
              css={{
                '&.ui.button': {
                  background: type !== 'Webinar' ? '#D91C5C' : '#6C2D8A',
                  color: '#fff',
                  width: 'max-content',
                  borderRadius: 5,
                },

                '&:hover': {
                  filter: 'brightness(85%)',
                  background: type !== 'Webinar' ? '#D91C5C' : '#6C2D8A',
                  color: '#fff',
                },
              }}
              onClick={() =>
                window
                  .open(webinarRecordingLink || registrationLink, '_blank')
                  .focus()
              }
            />
          </div>
        </div>
      </div>
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: isMdDown ? 'center' : 'flex-start',
          alignItems: 'center',
          background: '#F4F7F6',
          flex: 0.25,
          padding: isSmDown ? 20 : 0,
        }}
      >
        <div
          css={{
            background: '#fff',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '174px',
            height: '174px',
            borderRadius: '10px',
            border: '1px solid #E5E5E5',
            padding: '24px',
            marginTop: isMdDown ? 0 : '75px',
          }}
        >
          <Image
            src={organizerLogo}
            css={{
              width: '100%',
              objectFit: 'contain',
              backgroundRepeat: 'no-repeat',
            }}
          />
        </div>
        <p
          css={{
            fontSize: 11,
            fontStyle: 'italic',
            textAlign: 'center',
            marginTop: 15,
            marginBottom: 5,
          }}
        >
          Event organised by
        </p>
        <p
          css={{
            fontSize: 14,
            fontWeight: 700,
          }}
        >
          {organizerName}
        </p>
      </div>
    </div>
  );
};

EventItem.propTypes = {
  name: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  organizerLogo: PropTypes.string.isRequired,
  organizerName: PropTypes.string.isRequired,
  time: PropTypes.string.isRequired,
  tags: PropTypes.arrayOf(PropTypes.string).isRequired,
  registrationLink: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  duration: PropTypes.number.isRequired,
  eventImageLink: PropTypes.string.isRequired,
  webinarRecordingLink: PropTypes.string.isRequired,
};

export default EventItem;
